import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Sparkle,
  ArrowUpRight,
  TrendUp,
  CloudArrowUp,
  ArrowsClockwise,
  MagnifyingGlass,
  CaretLeft,
  CaretRight,
  ListChecks,
  XCircle,
  Eye,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 50;

const FIELD_LABELS = {
  name: 'Navn',
  description: 'Beskrivelse',
  short_description: 'Kort beskrivelse',
  meta_title: 'Meta-tittel',
  meta_description: 'Meta-beskrivelse',
  focus_keyword: 'Fokusnøkkelfrase',
  tags: 'Stikkord',
};

const SORT_OPTIONS = [
  { value: 'delta_desc', label: 'Største økning først' },
  { value: 'boosted_desc', label: 'Sist boostet først' },
  { value: 'score_asc', label: 'Lavest ny-score først' },
];

function ScoreDelta({ before, after }) {
  if (before == null && after == null) {
    return <span className="text-muted-foreground">—</span>;
  }
  const b = before ?? 0;
  const a = after ?? 0;
  const delta = a - b;
  const sign = delta > 0 ? '+' : '';
  const color = delta > 0 ? 'text-success' : delta < 0 ? 'text-destructive' : 'text-muted-foreground';
  return (
    <span className="tabular-nums">
      <span className="text-muted-foreground">{b}</span>
      <span className="mx-1">→</span>
      <span className="font-semibold">{a}</span>
      <span className={`ml-2 text-xs ${color}`}>({sign}{delta})</span>
    </span>
  );
}

/**
 * BoostReviewQueue — paginated review tab for AI Boost results.
 *
 * Shows products that have been processed by Boost with before/after SEO +
 * readability scores. Bulk push to Woo / discard local changes.
 *
 * Props:
 *   getHeaders: () => headers
 *   onChanged:  () => void  parent refresh (badge counts)
 *   refreshKey: number — bump to force refetch
 */
export default function BoostReviewQueue({ getHeaders, onChanged, refreshKey }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sort, setSort] = useState('delta_desc');
  const [includePushed, setIncludePushed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [expanded, setExpanded] = useState(new Set());

  const onChangedRef = useRef(onChanged);
  useEffect(() => { onChangedRef.current = onChanged; }, [onChanged]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(0); }, [debouncedSearch, sort, includePushed]);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: String(page * PAGE_SIZE),
        limit: String(PAGE_SIZE),
        sort,
        include_pushed: String(includePushed),
      });
      if (debouncedSearch) params.set('search', debouncedSearch);
      const { data } = await axios.get(
        `${API}/api/boost/reviewed?${params.toString()}`,
        { headers: getHeaders() },
      );
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch {
      // soft fail
    } finally {
      setLoading(false);
    }
  }, [page, sort, includePushed, debouncedSearch, getHeaders]);

  useEffect(() => { fetchPage(); }, [fetchPage, refreshKey]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const allIds = useMemo(() => items.map((i) => i.id), [items]);
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id));
  const someSelected = selected.size > 0 && !allSelected;

  const toggleSelectAll = () => {
    if (allSelected) {
      const next = new Set(selected);
      allIds.forEach((id) => next.delete(id));
      setSelected(next);
    } else {
      setSelected(new Set([...selected, ...allIds]));
    }
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleExpand = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpanded(next);
  };

  const refreshAndNotify = async () => {
    await fetchPage();
    onChangedRef.current && onChangedRef.current();
  };

  const pushOne = async (id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/boost/push/${id}`, {}, { headers: getHeaders() });
      toast.success('Pushet til WooCommerce');
      setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
      await refreshAndNotify();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Push feilet');
    } finally { setBusy(false); }
  };

  const discardOne = async (id) => {
    if (!window.confirm('Forkast lokale endringer og hent original fra WooCommerce?')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/boost/discard/${id}`, {}, { headers: getHeaders() });
      toast.success('Forkastet — original hentet');
      setSelected((prev) => { const n = new Set(prev); n.delete(id); return n; });
      await refreshAndNotify();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Forkasting feilet');
    } finally { setBusy(false); }
  };

  const pushSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Push ${selected.size} produkter til WooCommerce?`)) return;
    setBusy(true);
    try {
      // Sequential per id to avoid hammering the Woo API.
      let ok = 0, fail = 0;
      for (const id of Array.from(selected)) {
        try {
          await axios.post(`${API}/api/boost/push/${id}`, {}, { headers: getHeaders() });
          ok++;
        } catch { fail++; }
      }
      toast[fail ? 'warning' : 'success'](`Pushet ${ok}${fail ? `, ${fail} feilet` : ''}`);
      setSelected(new Set());
      await refreshAndNotify();
    } finally { setBusy(false); }
  };

  const pushAllMatching = async () => {
    if (total === 0) return;
    const scopeLabel = debouncedSearch ? `${total} treff på "${debouncedSearch}"` : `alle ${total} produkter`;
    if (!window.confirm(`Push ${scopeLabel} til WooCommerce på tvers av alle sider?`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/boost/push-all`,
        { search: debouncedSearch || null },
        { headers: getHeaders() },
      );
      toast.success(data.message || `${data.queued_count} pushes i bakgrunnen`);
      setSelected(new Set());
      await refreshAndNotify();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Push-alle feilet');
    } finally { setBusy(false); }
  };

  const discardAllMatching = async () => {
    if (total === 0) return;
    const scopeLabel = debouncedSearch ? `${total} treff på "${debouncedSearch}"` : `alle ${total} produkter`;
    if (!window.confirm(`Forkast ${scopeLabel} (henter fra Woo)? Kan ikke angres bulk.`)) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/boost/discard-all`,
        { search: debouncedSearch || null },
        { headers: getHeaders() },
      );
      toast.success(data.message || `${data.queued_count} forkastes`);
      setSelected(new Set());
      await refreshAndNotify();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Forkast-alle feilet');
    } finally { setBusy(false); }
  };

  if (!loading && total === 0 && !debouncedSearch && !includePushed) {
    return (
      <Card className="border border-border">
        <CardContent className="p-8 text-center text-muted-foreground">
          <Sparkle size={32} className="mx-auto mb-2 opacity-50" />
          <p className="text-sm">Ingen produkter venter på gjennomgang ennå.</p>
          <p className="text-xs mt-1">Send produkter til AI-foredling fra "Kjør boost"-fanen så vises de her med ny score.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="boost-review-queue">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="boost-review-toolbar">
        <div className="relative flex-1 min-w-[220px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Søk produktnavn..."
            className="pl-9"
            data-testid="boost-review-search"
          />
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-[220px]" data-testid="boost-review-sort">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Switch
            id="include-pushed"
            checked={includePushed}
            onCheckedChange={setIncludePushed}
            data-testid="boost-review-include-pushed"
          />
          <Label htmlFor="include-pushed" className="text-xs cursor-pointer">Vis pushede</Label>
        </div>
        <Badge variant="outline" className="text-xs" data-testid="boost-review-total">
          {total} totalt{debouncedSearch ? ' (filtrert)' : ''}
        </Badge>
      </div>

      {/* Bulk action bar */}
      <div
        className="flex items-center justify-between bg-muted/40 border border-border rounded-md px-4 py-2 sticky top-2 z-10 backdrop-blur-sm gap-3 flex-wrap"
        data-testid="boost-bulk-bar"
      >
        <div className="flex items-center gap-3">
          <Checkbox
            checked={allSelected ? true : someSelected ? 'indeterminate' : false}
            onCheckedChange={toggleSelectAll}
            data-testid="boost-select-all"
          />
          <span className="text-sm text-muted-foreground">
            {selected.size > 0 ? `${selected.size} valgt` : `Velg synlige (${items.length})`}
          </span>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={pushSelected}
            disabled={selected.size === 0 || busy}
            className="gap-1"
            data-testid="boost-push-selected"
          >
            <CloudArrowUp size={14} /> Push valgte
          </Button>
          <div className="w-px h-6 bg-border mx-1" aria-hidden />
          <Button
            size="sm"
            variant="outline"
            onClick={discardAllMatching}
            disabled={total === 0 || busy || includePushed}
            className="gap-1 text-destructive"
            title={includePushed ? 'Skru av "Vis pushede" for å forkaste' : 'Forkast hele filteret'}
            data-testid="boost-discard-all"
          >
            <ArrowsClockwise size={14} /> Forkast alle ({total})
          </Button>
          <Button
            size="sm"
            onClick={pushAllMatching}
            disabled={total === 0 || busy || includePushed}
            className="gap-1"
            title={includePushed ? 'Skru av "Vis pushede" for å pushe' : 'Push hele filteret'}
            data-testid="boost-push-all"
          >
            <ListChecks size={14} /> Push alle ({total})
          </Button>
        </div>
      </div>

      {loading && items.length === 0 && (
        <Card className="border border-border">
          <CardContent className="p-8 text-center text-muted-foreground text-sm">Laster...</CardContent>
        </Card>
      )}

      {!loading && items.length === 0 && (debouncedSearch || includePushed) && (
        <Card className="border border-border">
          <CardContent className="p-8 text-center text-muted-foreground text-sm">
            Ingen treff.
          </CardContent>
        </Card>
      )}

      {items.map((item) => {
        const isSelected = selected.has(item.id);
        const isExpanded = expanded.has(item.id);
        const isPushed = item.modified_locally === false;
        return (
          <Card
            key={item.id}
            className={`border transition-colors ${isSelected ? 'border-primary' : 'border-border'}`}
            data-testid={`boost-review-${item.id}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleOne(item.id)}
                    className="mt-1"
                    data-testid={`boost-select-${item.id}`}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{item.name}</p>
                    <div className="flex gap-3 flex-wrap mt-2 text-xs">
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <Sparkle size={11} weight="bold" /> SEO:
                        <ScoreDelta before={item.boost_score_before} after={item.boost_score_after} />
                      </span>
                      <span className="flex items-center gap-1 text-muted-foreground">
                        <TrendUp size={11} weight="bold" /> Lesbarhet:
                        <ScoreDelta before={item.boost_readability_before} after={item.boost_readability_after} />
                      </span>
                      {item.delta_total != null && item.delta_total !== 0 && (
                        <Badge variant={item.delta_total > 0 ? 'default' : 'destructive'} className="text-xs">
                          Δ {item.delta_total > 0 ? '+' : ''}{item.delta_total}
                        </Badge>
                      )}
                      {isPushed && (
                        <Badge variant="outline" className="text-xs gap-1">
                          <CloudArrowUp size={10} /> Pushet
                        </Badge>
                      )}
                    </div>
                    {item.boost_changed_fields && item.boost_changed_fields.length > 0 && (
                      <div className="flex gap-1 mt-2 flex-wrap">
                        {item.boost_changed_fields.map((f) => (
                          <Badge key={f} variant="secondary" className="text-[10px]">
                            {FIELD_LABELS[f] || f}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggleExpand(item.id)}
                    className="gap-1"
                    data-testid={`boost-toggle-${item.id}`}
                  >
                    <Eye size={12} /> {isExpanded ? 'Skjul' : 'Vis tekst'}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    asChild
                    className="gap-1"
                    data-testid={`boost-open-${item.id}`}
                  >
                    <a href={`/products/${item.id}`}>
                      <ArrowUpRight size={12} /> Åpne
                    </a>
                  </Button>
                  {!isPushed && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => discardOne(item.id)}
                        disabled={busy}
                        className="gap-1 text-destructive"
                        data-testid={`boost-discard-${item.id}`}
                      >
                        <XCircle size={12} /> Forkast
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => pushOne(item.id)}
                        disabled={busy}
                        className="gap-1"
                        data-testid={`boost-push-${item.id}`}
                      >
                        <CloudArrowUp size={12} /> Push
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {isExpanded && (
                <div className="space-y-3 mt-3 pt-3 border-t border-border/60">
                  {(item.boost_changed_fields || []).map((field) => {
                    const value = item[field];
                    if (value == null || value === '') return null;
                    const isHtml = typeof value === 'string' && /<[a-z][\s\S]*?>/i.test(value);
                    return (
                      <div key={field} className="bg-muted/30 p-3 rounded-md">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                          {FIELD_LABELS[field] || field}
                        </p>
                        {isHtml ? (
                          <div
                            className="prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: String(value) }}
                          />
                        ) : Array.isArray(value) ? (
                          <p className="text-sm">{value.map((v) => v?.name || v).join(', ')}</p>
                        ) : (
                          <p className="text-sm whitespace-pre-wrap">{String(value)}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          className="flex items-center justify-between bg-muted/30 border border-border rounded-md px-4 py-2"
          data-testid="boost-pagination"
        >
          <span className="text-xs text-muted-foreground">
            Side {page + 1} av {totalPages} · viser {items.length} av {total}
          </span>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="gap-1"
              data-testid="boost-prev-page"
            >
              <CaretLeft size={12} /> Forrige
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="gap-1"
              data-testid="boost-next-page"
            >
              Neste <CaretRight size={12} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
